import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Building2,
  Briefcase,
  Plus,
  MapPin,
  ArrowRight,
  Loader2,
  LayoutList,
  Map,
  LayoutGrid,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Navigation,
  Maximize2,
  Percent,
  Eye,
  EyeOff,
  Clock,
  Calendar,
  Star,
  Wifi,
  Cloud,
  Sun,
  CloudRain,
  BarChart3,
  Printer,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  UserCheck
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// 300 miles in meters
const MILES_300_IN_METERS = 300 * 1609.34;

// Component to fit map bounds to all markers
const FitBounds = ({ bounds }) => {
  const map = useMap();
  const boundsString = JSON.stringify(bounds);
  
  useEffect(() => {
    if (bounds && bounds.length > 0) {
      const leafletBounds = L.latLngBounds(bounds);
      // Add a small timeout to ensure the map is ready
      setTimeout(() => {
        map.fitBounds(leafletBounds, { padding: [50, 50], maxZoom: 10 });
      }, 100);
    }
  }, [boundsString, map]);
  
  return null;
};

// Component to center map on user location
const CenterOnLocation = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || 7);
    }
  }, [center, zoom, map]);
  return null;
};

// Fix for default marker icons in webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

import { API } from '../utils/api';

// Simple geocoding using Nominatim (OpenStreetMap)
const geocodeAddress = async (address) => {
  try {
    // Normalize address for better geocoding
    // Fix common issues: "LaPorte" -> "La Porte", ensure comma before state
    let normalizedAddress = address
      .replace(/LaPorte/gi, 'La Porte')
      .replace(/(\w)\s+(TX|CA|NC|FL|NY|GA|PA|OH|IL|MI|NJ|VA|WA|AZ|MA|TN|IN|MO|MD|WI|CO|MN|SC|AL|LA|KY|OR|OK|CT|UT|IA|NV|AR|MS|KS|NM|NE|WV|ID|HI|NH|ME|MT|RI|DE|SD|ND|AK|DC|VT|WY)\s+(\d{5})/gi, '$1, $2 $3');
    
    // Try normalized address first
    let response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(normalizedAddress)}&limit=1`
    );
    let data = await response.json();
    
    if (data && data.length > 0) {
      return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
    }
    
    // If full address fails, try street + city/state (drop the house number)
    const streetMatch = normalizedAddress.match(/^\d+\s+(.+?)(?:,|$)/);
    if (streetMatch) {
      const streetAndRest = streetMatch[1] + normalizedAddress.substring(streetMatch[0].length);
      response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(streetAndRest.trim())}&limit=1`
      );
      data = await response.json();
      
      if (data && data.length > 0) {
        return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
      }
    }
    
    // If still fails, try to extract city/state/zip and geocode that
    const parts = normalizedAddress.split(',');
    if (parts.length >= 2) {
      const cityStateZip = parts.slice(-2).join(',').trim();
      response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(cityStateZip)}&limit=1`
      );
      data = await response.json();
      
      if (data && data.length > 0) {
        return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
      }
    }
    
    // Last resort: just try to find the zip code
    const zipMatch = normalizedAddress.match(/\d{5}/);
    if (zipMatch) {
      response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${zipMatch[0]}&limit=1`
      );
      data = await response.json();
      
      if (data && data.length > 0) {
        return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
      }
    }
  } catch (error) {
    console.error('Geocoding error:', error);
  }
  return null;
};

// Helper function to check if an address is valid for geocoding (not a placeholder)
const isValidGeocodableAddress = (address) => {
  if (!address || typeof address !== 'string') return false;
  
  const trimmedAddress = address.trim().toUpperCase();
  
  // List of invalid/placeholder address values
  const invalidAddresses = [
    'HOLDING COMPANY',
    'HOLDING CO',
    'N/A',
    'NA',
    'NONE',
    'TBD',
    'TO BE DETERMINED',
    'NOT APPLICABLE',
    'NO ADDRESS',
    '-',
    ''
  ];
  
  if (invalidAddresses.includes(trimmedAddress)) return false;
  
  // Address should contain at least a number and some letters (basic street address pattern)
  // This filters out single words like "HOLDING COMPANY"
  const hasNumber = /\d/.test(address);
  const hasMultipleWords = address.trim().split(/\s+/).length >= 2;
  
  return hasNumber && hasMultipleWords;
};

const Dashboard = () => {
  const { getAuthHeaders, user } = useAuth();
  const [llcs, setLLCs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    // Load saved view mode from localStorage, default to 'list'
    return localStorage.getItem('dashboardViewMode') || 'list';
  });
  const [sortConfig, setSortConfig] = useState(() => {
    // Load saved sort config from localStorage
    const saved = localStorage.getItem('dashboardSortConfig');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return { key: 'name', direction: 'asc' };
      }
    }
    return { key: 'name', direction: 'asc' };
  });
  const [geocodedLLCs, setGeocodedLLCs] = useState({});
  const [geocodingInProgress, setGeocodingInProgress] = useState(false);
  const [mapViewMode, setMapViewMode] = useState('all'); // 'all' or 'nearby'
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [userOwnerships, setUserOwnerships] = useState({}); // LLC id -> ownership percentage
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showMyLLCsOnly, setShowMyLLCsOnly] = useState(() => {
    return localStorage.getItem('dashboardShowMyLLCsOnly') === 'true';
  });
  const [hideNumbers, setHideNumbers] = useState(() => {
    return localStorage.getItem('dashboardHideNumbers') === 'true';
  });
  const [formData, setFormData] = useState({
    name: '',
    llc_type: 'real_estate',
    property_address: '',
    property_description: '',
    cash_balance: 0,
    status: 'active',
    hide_percentages: false,
    issue_financials: true
  });
  const [currentTime, setCurrentTime] = useState(new Date());
  const [ipAddress, setIpAddress] = useState('');
  const [weather, setWeather] = useState(null);
  const [stockMarket, setStockMarket] = useState(null);
  const [expandedMobileLlc, setExpandedMobileLlc] = useState(null);

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch IP address, weather, and stock market data
  useEffect(() => {
    // Get IP address
    fetch('https://api.ipify.org?format=json')
      .then(res => res.json())
      .then(data => setIpAddress(data.ip))
      .catch(() => setIpAddress(''));

    // Get weather using Open-Meteo (free, no API key, CORS enabled)
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          fetch(`https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current_weather=true&temperature_unit=fahrenheit`)
            .then(res => res.json())
            .then(data => {
              if (data.current_weather) {
                const temp = Math.round(data.current_weather.temperature);
                const code = data.current_weather.weathercode;
                // Weather codes: 0=clear, 1-3=cloudy, 45-48=fog, 51-67=rain, 71-77=snow, 80-82=showers, 95-99=thunderstorm
                let condition = 'Clear';
                if (code >= 1 && code <= 3) condition = 'Cloudy';
                else if (code >= 45 && code <= 48) condition = 'Foggy';
                else if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) condition = 'Rainy';
                else if (code >= 71 && code <= 77) condition = 'Snowy';
                else if (code >= 95) condition = 'Stormy';
                setWeather({ temp: `${temp}°F`, condition });
              }
            })
            .catch(() => setWeather(null));
        },
        () => {
          // Geolocation denied - use default location (NYC)
          fetch('https://api.open-meteo.com/v1/forecast?latitude=40.7128&longitude=-74.006&current_weather=true&temperature_unit=fahrenheit')
            .then(res => res.json())
            .then(data => {
              if (data.current_weather) {
                const temp = Math.round(data.current_weather.temperature);
                setWeather({ temp: `${temp}°F`, condition: 'NYC' });
              }
            })
            .catch(() => setWeather(null));
        }
      );
    }

    // Get S&P 500 from alternative free API
    fetch('https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd&include_24hr_change=true')
      .then(res => res.json())
      .then(data => {
        if (data.bitcoin) {
          const change = data.bitcoin.usd_24h_change;
          setStockMarket({
            symbol: 'BTC',
            changePercent: change?.toFixed(2),
            isUp: change >= 0
          });
        }
      })
      .catch(() => setStockMarket(null));
  }, []);

  // Format date and time
  const formatDateTime = () => {
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      timeZone: 'America/Chicago'
    };
    const dateStr = currentTime.toLocaleDateString('en-US', options);
    const timeStr = currentTime.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'America/Chicago'
    });
    return { dateStr, timeStr };
  };

  // Toggle number visibility
  const toggleHideNumbers = () => {
    const newValue = !hideNumbers;
    setHideNumbers(newValue);
    localStorage.setItem('dashboardHideNumbers', String(newValue));
  };

  // Print Portfolio Overview
  const handlePrint = () => {
    window.print();
  };

  // Mask number for privacy
  const maskNumber = (value) => {
    if (hideNumbers) return '••••••';
    return value;
  };

  // Get user's current location
  const getUserLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setMapViewMode('nearby');
        setLocationLoading(false);
        toast.success('Location found!');
      },
      (error) => {
        console.error('Geolocation error:', error);
        toast.error('Unable to get your location');
        setLocationLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const showAllLocations = () => {
    setMapViewMode('all');
    setUserLocation(null); // Clear user location to remove circle
  };

  // Toggle location view - if already showing nearby, switch to all
  const toggleLocationView = () => {
    if (mapViewMode === 'nearby') {
      showAllLocations();
    } else {
      getUserLocation();
    }
  };

  const fetchData = async (isManualRefresh = false) => {
    if (isManualRefresh) setIsRefreshing(true);
    try {
      const llcsRes = await axios.get(`${API}/llcs`, { headers: getAuthHeaders() });
      setLLCs(llcsRes.data);
      
      // Fetch user's ownership percentages for My Value column
      try {
        const ownershipsRes = await axios.get(`${API}/ownerships`, { headers: getAuthHeaders() });
        const ownershipMap = {};
        ownershipsRes.data.forEach(o => {
          // Match by investor name since we may not have investor_id directly
          if (o.investor_name === user.name) {
            ownershipMap[o.llc_id] = o.percentage;
          }
        });
        setUserOwnerships(ownershipMap);
      } catch (err) {
        console.error('Failed to fetch ownerships:', err);
      }
      if (isManualRefresh) toast.success('Dashboard refreshed');
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchData(true);
  };

  useEffect(() => {
    fetchData();
    
    // Refresh data when the page becomes visible again (user returns to tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchData();
      }
    };
    
    // Refresh data when window gains focus (user returns from another page/app)
    const handleFocus = () => {
      fetchData();
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Save view mode to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('dashboardViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem('dashboardShowMyLLCsOnly', showMyLLCsOnly);
  }, [showMyLLCsOnly]);

  // Save sort config to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('dashboardSortConfig', JSON.stringify(sortConfig));
  }, [sortConfig]);

  // Geocode addresses when switching to map view
  useEffect(() => {
    if (viewMode === 'map' && llcs.length > 0) {
      const geocodeLLCs = async () => {
        setGeocodingInProgress(true);
        const newGeocodedLLCs = {};
        const llcsToGeocode = llcs.filter(llc => isValidGeocodableAddress(llc.property_address));
        
        for (let i = 0; i < llcsToGeocode.length; i++) {
          const llc = llcsToGeocode[i];
          const coords = await geocodeAddress(llc.property_address);
          if (coords) {
            newGeocodedLLCs[llc.id] = coords;
          }
          // Update state after each geocode so map updates progressively
          setGeocodedLLCs(prev => ({ ...prev, ...newGeocodedLLCs }));
          // Longer delay to avoid rate limiting
          if (i < llcsToGeocode.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
        
        setGeocodingInProgress(false);
      };
      
      geocodeLLCs();
    }
  }, [viewMode, llcs]);

  // Calculate map center and bounds
  const mapCenter = useMemo(() => {
    const coords = Object.values(geocodedLLCs);
    if (coords.length === 0) {
      return [39.8, -98.5]; // Default to center of US
    }
    const avgLat = coords.reduce((sum, c) => sum + c.lat, 0) / coords.length;
    const avgLng = coords.reduce((sum, c) => sum + c.lng, 0) / coords.length;
    return [avgLat, avgLng];
  }, [geocodedLLCs]);

  // Calculate bounds for fitting all markers
  const mapBounds = useMemo(() => {
    const coords = Object.values(geocodedLLCs);
    if (coords.length === 0) return null;
    return coords.map(c => [c.lat, c.lng]);
  }, [geocodedLLCs]);

  const llcsWithAddresses = llcs.filter(llc => isValidGeocodableAddress(llc.property_address));

  // Sorting function
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sorted LLCs for list view
  const sortedLLCs = useMemo(() => {
    let source = llcs;
    if (showMyLLCsOnly && user?.role === 'manager') {
      source = llcs.filter(llc => userOwnerships[llc.id] != null);
    }
    const sorted = [...source].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle string comparison
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal?.toLowerCase() || '';
      }
      
      // Handle null/undefined
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      
      // Primary sort comparison
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      
      // Secondary sort: always sort by name alphabetically when primary values are equal
      if (sortConfig.key !== 'name') {
        const aName = (a.name || '').toLowerCase();
        const bName = (b.name || '').toLowerCase();
        if (aName < bName) return -1;
        if (aName > bName) return 1;
      }
      
      return 0;
    });
    return sorted;
  }, [llcs, sortConfig, showMyLLCsOnly, user?.role, userOwnerships]);

  const handleCreateLLC = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      await axios.post(`${API}/llcs`, formData, { headers: getAuthHeaders() });
      toast.success('LLC created successfully');
      setDialogOpen(false);
      setFormData({
        name: '',
        llc_type: 'real_estate',
        property_address: '',
        property_description: '',
        cash_balance: 0,
        status: 'active',
        hide_percentages: false,
        issue_financials: true
      });
      fetchData();
    } catch (error) {
      console.error('Create error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create LLC');
    } finally {
      setFormLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const val = Math.abs(amount) < 0.5 ? 0 : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(val);
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100',
      inactive: 'bg-slate-100 text-slate-600 hover:bg-slate-100',
      liquidating: 'bg-amber-100 text-amber-800 hover:bg-amber-100'
    };
    return (
      <Badge variant="outline" className={variants[status] || variants.active}>
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Info Banner */}
      <div className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 text-white rounded-lg px-4 py-3 shadow-md print:hidden" data-testid="info-banner">
        <div className="flex items-center justify-between flex-wrap gap-3">
          {/* Date */}
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-amber-500/20 rounded-full flex items-center justify-center">
              <Calendar className="w-3.5 h-3.5 text-amber-400" />
            </div>
            <span className="text-sm font-medium">{formatDateTime().dateStr}</span>
          </div>
          
          {/* Time */}
          <div className="flex items-center gap-2 bg-slate-900/50 px-3 py-1.5 rounded-full">
            <Clock className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-sm font-mono tracking-wider">{formatDateTime().timeStr}</span>
          </div>

          {/* IP Address */}
          {ipAddress && (
            <div className="flex items-center gap-2 bg-slate-900/30 px-3 py-1.5 rounded-full">
              <Wifi className="w-3.5 h-3.5 text-blue-400" />
              <span className="text-xs font-mono text-slate-300">{ipAddress}</span>
            </div>
          )}

          {/* Weather */}
          {weather && (
            <div className="flex items-center gap-2 bg-slate-900/30 px-3 py-1.5 rounded-full">
              <Cloud className="w-3.5 h-3.5 text-sky-400" />
              <span className="text-sm text-slate-200">{weather.temp}</span>
              {weather.condition && <span className="text-xs text-slate-400">{weather.condition}</span>}
            </div>
          )}

          {/* Stock Market */}
          {stockMarket && (
            <div className="flex items-center gap-2 bg-slate-900/30 px-3 py-1.5 rounded-full">
              <BarChart3 className="w-3.5 h-3.5 text-orange-400" />
              <span className="text-xs text-slate-300">{stockMarket.symbol}</span>
              <span className={`text-sm font-mono ${stockMarket.isUp ? 'text-emerald-400' : 'text-rose-400'}`}>
                {stockMarket.isUp ? '+' : ''}{stockMarket.changePercent}%
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900">
            Portfolio Overview
          </h1>
          <p className="text-slate-500 mt-1">
            Welcome back, <span className="font-semibold text-amber-700">{user?.name}</span>. {user?.role === 'viewer' || user?.role === 'professional' ? 'View' : 'Manage'} your Investments, JVs & LLCs.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Refresh Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="px-3 rounded-full text-slate-600 hover:text-slate-800 hover:bg-slate-100 print:hidden"
            title="Refresh Dashboard"
            data-testid="refresh-dashboard-btn"
          >
            <RefreshCw className={`w-6 h-6 stroke-[2.5] ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
          
          {/* Print Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePrint}
            className="px-3 rounded-full text-slate-600 hover:text-slate-800 hover:bg-slate-100 print:hidden"
            title="Print Portfolio Overview"
            data-testid="print-portfolio-btn"
          >
            <Printer className="w-6 h-6 stroke-[2.5]" />
          </Button>
          
          {/* Privacy Toggle */}
          {user?.role === 'manager' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowMyLLCsOnly(prev => !prev)}
              className={`px-3 rounded-full print:hidden ${showMyLLCsOnly ? 'text-amber-600 bg-amber-100 hover:bg-amber-200' : 'text-slate-600 hover:text-slate-800 hover:bg-slate-100'}`}
              title={showMyLLCsOnly ? 'Show all LLCs' : 'Show only my LLCs'}
              data-testid="toggle-my-llcs-btn"
            >
              <UserCheck className="w-6 h-6 stroke-[2.5]" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleHideNumbers}
            className={`px-3 rounded-full print:hidden ${hideNumbers ? 'text-amber-600 bg-amber-100 hover:bg-amber-200' : 'text-slate-600 hover:text-slate-800 hover:bg-slate-100'}`}
            title={hideNumbers ? 'Show numbers' : 'Hide numbers'}
            data-testid="toggle-numbers-btn"
          >
            {hideNumbers ? <EyeOff className="w-6 h-6 stroke-[2.5]" /> : <Eye className="w-6 h-6 stroke-[2.5]" />}
          </Button>
          
          {/* View Toggle */}
          <div className="flex items-center bg-slate-200 rounded-full p-1 gap-0.5 print:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('list')}
              className={`px-3 rounded-full ${viewMode === 'list' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:bg-white/50'}`}
              data-testid="view-list-btn"
            >
              <LayoutList className="w-4 h-4 mr-1.5" />
              List
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('panels')}
              className={`px-3 rounded-full ${viewMode === 'panels' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:bg-white/50'}`}
              data-testid="view-panels-btn"
            >
              <LayoutGrid className="w-4 h-4 mr-1.5" />
              Panels
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('map')}
              className={`px-3 rounded-full ${viewMode === 'map' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:bg-white/50'}`}
              data-testid="view-map-btn"
            >
              <Map className="w-4 h-4 mr-1.5" />
              Map
            </Button>
          </div>
          
          {(user?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'manager') && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-amber-600 hover:bg-amber-700 shadow-md" data-testid="add-llc-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add LLC
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle className="text-xl font-semibold">Create New LLC</DialogTitle>
                  <DialogDescription>
                    Add a new LLC to your portfolio
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateLLC}>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g., Maple Street Holdings LLC"
                        required
                        data-testid="llc-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="type">Type</Label>
                      <Select
                        value={formData.llc_type}
                        onValueChange={(value) => setFormData({ ...formData, llc_type: value })}
                      >
                        <SelectTrigger data-testid="llc-type-select">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="real_estate">Real Estate Property</SelectItem>
                          <SelectItem value="cash_balance">Liquidating Cash Balance</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {formData.llc_type === 'real_estate' && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="address">Property Address</Label>
                          <Input
                            id="address"
                            value={formData.property_address}
                            onChange={(e) => setFormData({ ...formData, property_address: e.target.value })}
                            placeholder="123 Main St, City, State ZIP"
                            data-testid="llc-address-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="description">Property Description</Label>
                          <Textarea
                            id="description"
                            value={formData.property_description}
                            onChange={(e) => setFormData({ ...formData, property_description: e.target.value })}
                            placeholder="Brief description of the property..."
                            rows={3}
                            data-testid="llc-description-input"
                          />
                        </div>
                      </>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="balance">Initial Cash Balance</Label>
                      <Input
                        id="balance"
                        type="number"
                        value={formData.cash_balance}
                        onChange={(e) => setFormData({ ...formData, cash_balance: parseFloat(e.target.value) || 0 })}
                        placeholder="0"
                        data-testid="llc-balance-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="status">Status</Label>
                      <Select
                        value={formData.status}
                        onValueChange={(value) => setFormData({ ...formData, status: value })}
                      >
                        <SelectTrigger data-testid="llc-status-select">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                          <SelectItem value="liquidating">Liquidating</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center space-x-2 pt-2">
                      <Checkbox
                        id="hide-percentages"
                        checked={formData.hide_percentages}
                        onCheckedChange={(checked) => setFormData({ ...formData, hide_percentages: checked })}
                        data-testid="hide-percentages-checkbox"
                      />
                      <Label htmlFor="hide-percentages" className="text-sm font-normal cursor-pointer">
                        Hide ownership percentages from viewers
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2 pt-2">
                      <Checkbox
                        id="issue-financials"
                        checked={formData.issue_financials !== false}
                        onCheckedChange={(checked) => setFormData({ ...formData, issue_financials: !!checked })}
                        data-testid="issue-financials-checkbox"
                      />
                      <Label htmlFor="issue-financials" className="text-sm font-normal cursor-pointer">
                        Issue financial statements (include in Quarterly Reports email)
                      </Label>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="bg-slate-900 hover:bg-slate-800"
                      disabled={formLoading}
                      data-testid="llc-submit-btn"
                    >
                      {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create LLC'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* LLC Grid or Map */}
      <div>
        <div className="flex items-center justify-between mb-4 bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 rounded-lg px-5 py-3.5 shadow-md" data-testid="llc-header-banner">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Your Investments, JVs & LLCs</h2>
              <p className="text-xs text-slate-400">Portfolio summary</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-slate-900/50 px-5 py-2 rounded-lg text-center">
              <p className="text-lg font-bold text-white">{sortedLLCs.length}</p>
              <p className="text-xs text-slate-400 font-medium">{sortedLLCs.length === 1 ? 'Entity' : 'Entities'}</p>
            </div>
            {Object.keys(userOwnerships).length > 0 && (
              <div className="bg-slate-900/50 px-5 py-2 rounded-lg text-center">
                <p className="text-lg font-bold text-white">
                  {hideNumbers ? '••••••' : formatCurrency(
                    llcs.reduce((sum, llc) => {
                      const ownership = userOwnerships[llc.id];
                      if (!ownership) return sum;
                      
                      // Check for custom valuation values in localStorage
                      const savedRatePerSqft = localStorage.getItem(`valuation_ratePerSqft_${llc.id}`);
                      const savedCapRate = localStorage.getItem(`valuation_capRate_${llc.id}`);
                      
                      let netValue = 0;
                      if (llc.llc_type === 'real_estate' && llc.lease_sqft) {
                        // Use saved values or defaults
                        const ratePerSqft = savedRatePerSqft ? parseFloat(savedRatePerSqft) : llc.lease_rate_per_sqft;
                        const capRatePercent = savedCapRate ? parseFloat(savedCapRate) : 6;
                        
                        if (ratePerSqft) {
                          const annualRents = llc.lease_sqft * ratePerSqft;
                          const capRate = capRatePercent / 100;
                          const grossValue = annualRents / capRate;
                          const cashBalance = llc.cash_balance || 0;
                          const liabilities = llc.total_liabilities || 0;
                          netValue = grossValue + cashBalance - liabilities;
                        }
                      } else {
                        netValue = llc.cash_balance || 0;
                      }
                      
                      return sum + (netValue * (ownership / 100));
                    }, 0)
                  )}
                </p>
                <p className="text-xs text-slate-400 font-medium">My Total Value</p>
              </div>
            )}
          </div>
        </div>
        {llcs.length === 0 ? (
          <Card className="border-dashed border-2 border-amber-300 bg-amber-50/50">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-700">No LLCs yet</h3>
              <p className="text-slate-500 mt-1 mb-4">Get started by adding your first LLC</p>
              {(user?.role === 'superadmin' || user?.role === 'admin') && (
                <Button
                  onClick={() => setDialogOpen(true)}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add First LLC
                </Button>
              )}
            </CardContent>
          </Card>
        ) : viewMode === 'panels' ? (
          /* Panels/Cards View */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedLLCs.map((llc, index) => (
              <Link
                key={llc.id}
                to={`/llc/${llc.id}`}
                className={`animate-fade-in stagger-${(index % 4) + 1}`}
                data-testid={`llc-card-${llc.id}`}
              >
                <Card className={`card-hover h-full flex flex-col overflow-hidden ${
                  llc.status === 'active' ? 'border-l-4 border-l-emerald-500 border-slate-200' :
                  llc.status === 'liquidating' ? 'border-l-4 border-l-amber-500 border-slate-200' :
                  'border-l-4 border-l-slate-400 border-slate-200'
                }`}>
                  <CardHeader className="pb-1 pt-2 px-3 bg-gradient-to-r from-slate-50 to-white">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className={`w-7 h-7 rounded flex items-center justify-center flex-shrink-0 ${
                          llc.llc_type === 'real_estate' ? 'bg-indigo-100' : 'bg-amber-100'
                        }`}>
                          {llc.llc_type === 'real_estate' ? (
                            <Building2 className="w-4 h-4 text-indigo-600" />
                          ) : (
                            <Briefcase className="w-4 h-4 text-amber-600" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1">
                            <CardTitle className="text-sm font-semibold truncate leading-tight">{llc.name}</CardTitle>
                            {llc.info_current && (
                              <Star className="w-3 h-3 fill-amber-400 text-amber-400 flex-shrink-0" />
                            )}
                            {llc.distributions_current && (
                              <Star className="w-3 h-3 fill-blue-400 text-blue-400 flex-shrink-0" />
                            )}
                          </div>
                          <CardDescription className="capitalize text-xs leading-tight">
                            {llc.llc_type.replace('_', ' ')}
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {getStatusBadge(llc.status)}
                        {user?.role === 'viewer' && userOwnerships[llc.id] && (
                          <Badge className="bg-blue-100 text-blue-800 border-blue-200 whitespace-nowrap text-xs px-1.5 py-0">
                            {llc.hide_percentages ? 'Calc' : (hideNumbers ? '••••' : `${Number(userOwnerships[llc.id]).toFixed(2)}%`)}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col px-3 pb-2 pt-0">
                    {/* Address - compact */}
                    <div className="h-6 mb-1">
                      {llc.property_address ? (
                        <div className="flex items-center gap-1 text-xs text-slate-500 truncate">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{llc.property_address}</span>
                        </div>
                      ) : (
                        <div className="text-xs text-slate-400 italic">No address</div>
                      )}
                    </div>
                    
                    {/* Financials - compact */}
                    <div className="space-y-0.5 pt-1 border-t border-slate-100 flex-1">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-slate-500">Cash</span>
                        <span className="font-mono text-xs font-medium text-slate-900">
                          {hideNumbers ? '••••' : formatCurrency(llc.cash_balance)}
                        </span>
                      </div>
                      
                      {/* Ownership bar for investors */}
                      {user?.role === 'viewer' && userOwnerships[llc.id] && (
                        <div className="flex justify-between items-center pt-1 mt-0.5 border-t border-slate-100">
                          <span className="text-xs text-blue-700">Ownership</span>
                          <div className="flex items-center gap-1.5">
                            {!hideNumbers && !llc.hide_percentages && (
                              <div className="w-12 h-1 bg-blue-100 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-blue-600 rounded-full"
                                  style={{ width: `${userOwnerships[llc.id]}%` }}
                                />
                              </div>
                            )}
                            <span className="font-mono font-bold text-blue-700 text-xs">
                              {llc.hide_percentages ? <span className="text-slate-400 italic font-normal">Calc</span> : (hideNumbers ? '••••' : `${Number(userOwnerships[llc.id]).toFixed(2)}%`)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="mt-1.5 pt-1.5 border-t border-slate-100 flex justify-end">
                      <span className="text-xs text-amber-600 font-medium flex items-center gap-0.5 hover:gap-1 transition-all">
                        Details
                        <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : viewMode === 'list' ? (
          /* List/Table View */
          <Card className="border-slate-200 shadow-sm">
            {/* Mobile Card View - visible on small screens only */}
            <div className="md:hidden">
              {/* Mobile Header */}
              <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-3 py-3">
                <div className="grid grid-cols-12 gap-1 text-white text-xs font-semibold">
                  <div className="col-span-6">Name</div>
                  <div className="col-span-3">Type</div>
                  <div className="col-span-3 text-right">Ownership</div>
                </div>
              </div>
              {/* Mobile Rows */}
              <div className="divide-y divide-slate-100">
                {sortedLLCs.map((llc) => {
                  const isExpanded = expandedMobileLlc === llc.id;
                  const ownership = userOwnerships[llc.id];
                  
                  // Calculate My Value (same logic as desktop)
                  const calculateMyValue = () => {
                    if (!ownership) return null;
                    const savedRatePerSqft = localStorage.getItem(`valuation_ratePerSqft_${llc.id}`);
                    const savedCapRate = localStorage.getItem(`valuation_capRate_${llc.id}`);
                    let netValue = 0;
                    if (llc.llc_type === 'real_estate' && llc.lease_sqft) {
                      const ratePerSqft = savedRatePerSqft ? parseFloat(savedRatePerSqft) : llc.lease_rate_per_sqft;
                      const capRatePercent = savedCapRate ? parseFloat(savedCapRate) : 6;
                      if (ratePerSqft) {
                        const annualRents = llc.lease_sqft * ratePerSqft;
                        const capRate = capRatePercent / 100;
                        const grossValue = annualRents / capRate;
                        const cashBalance = llc.cash_balance || 0;
                        const liabilities = llc.third_party_liabilities || 0;
                        netValue = grossValue + cashBalance - liabilities;
                      }
                    } else {
                      netValue = llc.cash_balance || 0;
                    }
                    return netValue * (ownership / 100);
                  };
                  const myValue = calculateMyValue();

                  return (
                    <div key={llc.id} className={`${llc.distributions_current ? 'bg-blue-50/50 border-l-3 border-l-blue-500' : llc.info_current ? 'bg-amber-50/50 border-l-3 border-l-amber-500' : ''}`}>
                      {/* Collapsed Row - always visible */}
                      <div 
                        className="px-3 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => setExpandedMobileLlc(isExpanded ? null : llc.id)}
                        data-testid={`mobile-llc-row-${llc.id}`}
                      >
                        <div className="grid grid-cols-12 gap-1 items-center">
                          <div className="col-span-6 flex items-center gap-1.5 min-w-0">
                            <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                              llc.llc_type === 'real_estate' ? 'bg-slate-100' : 'bg-amber-100'
                            }`}>
                              {llc.llc_type === 'real_estate' ? (
                                <Building2 className="w-3 h-3 text-slate-700" />
                              ) : (
                                <Briefcase className="w-3 h-3 text-amber-700" />
                              )}
                            </div>
                            <span className="font-medium text-slate-900 text-sm truncate">{llc.name}</span>
                            {llc.info_current && (
                              <Star className="w-3 h-3 fill-amber-400 text-amber-400 flex-shrink-0" />
                            )}
                            {llc.distributions_current && (
                              <Star className="w-3 h-3 fill-blue-400 text-blue-400 flex-shrink-0" />
                            )}
                          </div>
                          <div className="col-span-3 text-xs text-slate-600 capitalize truncate">
                            {llc.llc_type.replace('_', ' ')}
                          </div>
                          <div className="col-span-3 flex items-center justify-end gap-0.5">
                            <span className="font-mono text-sm text-amber-700">
                              {ownership ? (
                                llc.hide_percentages ? (
                                  <span className="text-slate-400 italic text-xs">Calc</span>
                                ) : (
                                  hideNumbers ? '••••' : `${Number(ownership).toFixed(2)}%`
                                )
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </span>
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4 text-slate-400" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="px-4 pb-3 pt-0 bg-slate-50/50 border-t border-slate-100">
                          <div className="grid grid-cols-2 gap-x-4 gap-y-2 py-2 text-sm">
                            <div>
                              <span className="text-slate-500 text-xs">Cash Balance</span>
                              <p className="font-mono font-medium">{hideNumbers ? '••••••' : formatCurrency(llc.cash_balance)}</p>
                            </div>
                            <div>
                              <span className="text-slate-500 text-xs">Total Assets</span>
                              <p className="font-mono font-medium text-emerald-700">{hideNumbers ? '••••••' : formatCurrency(llc.total_assets)}</p>
                            </div>
                            <div>
                              <span className="text-slate-500 text-xs">3P Liabilities</span>
                              <p className="font-mono font-medium text-rose-600">{hideNumbers ? '••••••' : formatCurrency(llc.third_party_liabilities || 0)}</p>
                            </div>
                            <div>
                              <span className="text-slate-500 text-xs">My Value</span>
                              <p className="font-mono font-medium text-indigo-700">
                                {myValue !== null ? (hideNumbers ? '••••••' : formatCurrency(myValue)) : <span className="text-slate-400">—</span>}
                              </p>
                            </div>
                            <div>
                              <span className="text-slate-500 text-xs">Last Activity</span>
                              <p className={`font-medium ${
                                llc.last_activity_date && llc.last_activity_date !== '-' ? (() => {
                                  const [year, month, day] = llc.last_activity_date.split('-').map(Number);
                                  const activityDate = new Date(year, month - 1, day);
                                  const today = new Date();
                                  today.setHours(0, 0, 0, 0);
                                  const daysDiff = Math.floor((today - activityDate) / (24 * 60 * 60 * 1000));
                                  if (daysDiff <= 30) return 'text-emerald-700';
                                  if (daysDiff <= 90) return 'text-amber-600';
                                  if (daysDiff > 365) return 'text-rose-600';
                                  return 'text-slate-500';
                                })() : 'text-slate-400'
                              }`}>
                                {llc.last_activity_date && llc.last_activity_date !== '-' ? llc.last_activity_date : '-'}
                              </p>
                            </div>
                            <div>
                              <span className="text-slate-500 text-xs">Last Accounting</span>
                              <p className={`font-medium ${
                                llc.last_accounting_date && llc.last_accounting_date !== '-' ? (() => {
                                  const [year, month, day] = llc.last_accounting_date.split('-').map(Number);
                                  const accountingDate = new Date(year, month - 1, day);
                                  const today = new Date();
                                  today.setHours(0, 0, 0, 0);
                                  const daysDiff = Math.floor((today - accountingDate) / (24 * 60 * 60 * 1000));
                                  if (daysDiff <= 30) return 'text-emerald-700';
                                  if (daysDiff <= 90) return 'text-amber-600';
                                  if (daysDiff > 365) return 'text-rose-600';
                                  return 'text-slate-500';
                                })() : 'text-slate-400'
                              }`}>
                                {llc.last_accounting_date && llc.last_accounting_date !== '-' ? llc.last_accounting_date : '-'}
                              </p>
                            </div>
                            {user?.role !== 'viewer' && (
                              <>
                                <div>
                                  <span className="text-slate-500 text-xs">Total Income</span>
                                  <p className="font-mono font-medium text-emerald-600">{hideNumbers ? '••••••' : formatCurrency(llc.total_income)}</p>
                                </div>
                                <div>
                                  <span className="text-slate-500 text-xs">Total Expenses</span>
                                  <p className="font-mono font-medium text-rose-600">{hideNumbers ? '••••••' : formatCurrency(llc.total_expenses)}</p>
                                </div>
                              </>
                            )}
                          </div>
                          <Link 
                            to={`/llc/${llc.id}`}
                            className="flex items-center justify-center gap-2 mt-2 py-2 bg-amber-600 text-white rounded-md text-sm font-medium hover:bg-amber-700 transition-colors"
                          >
                            View Details
                            <ArrowRight className="w-4 h-4" />
                          </Link>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Desktop Table View - hidden on mobile */}
            <div className="hidden md:block">
            {/* Print-only table header - using colgroup for consistent widths */}
            {/* 9 columns: Name, Type, Ownership, Cash Balance, Total Assets, 3P Liabilities, My Value, Last Activity, Last Accounting */}
            <table className="print-header-table w-full border-collapse" data-testid="print-table-header">
              <colgroup>
                <col style={{width: '17%'}} />
                <col style={{width: '10%'}} />
                <col style={{width: '9%'}} />
                <col style={{width: '11%'}} />
                <col style={{width: '11%'}} />
                <col style={{width: '10%'}} />
                <col style={{width: '10%'}} />
                <col style={{width: '11%'}} />
                <col style={{width: '11%'}} />
              </colgroup>
              <thead>
                <tr className="bg-slate-800 text-white text-xs font-semibold">
                  <th className="px-2 py-2 text-left">Name</th>
                  <th className="px-2 py-2 text-left">Type</th>
                  <th className="px-2 py-2 text-center">Ownership</th>
                  <th className="px-2 py-2 text-right">Cash Balance</th>
                  <th className="px-2 py-2 text-right">Total Assets</th>
                  <th className="px-2 py-2 text-right">3P Liabilities</th>
                  <th className="px-2 py-2 text-right">My Value</th>
                  <th className="px-2 py-2 text-center">Last Activity</th>
                  <th className="px-2 py-2 text-center">Last Accounting</th>
                </tr>
              </thead>
            </table>
            <Table className="print-data-table w-full">
              <colgroup className="print-colgroup">
                <col style={{width: '17%'}} />
                <col style={{width: '10%'}} />
                <col style={{width: '9%'}} />
                <col style={{width: '11%'}} />
                <col style={{width: '11%'}} />
                <col style={{width: '10%'}} />
                <col style={{width: '10%'}} />
                <col style={{width: '11%'}} />
                <col style={{width: '11%'}} />
              </colgroup>
              <TableHeader className="screen-only-header">
                <TableRow className="bg-gradient-to-r from-slate-800 to-slate-700">
                  <TableHead className="text-white font-semibold text-xs xl:text-sm">
                    <button 
                      onClick={() => handleSort('name')}
                      className="flex items-center gap-1 hover:text-amber-300 transition-colors"
                    >
                      Name
                      {sortConfig.key === 'name' ? (
                        sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3 text-amber-400" /> : <ArrowDown className="w-3 h-3 text-amber-400" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 text-slate-400" />
                      )}
                    </button>
                  </TableHead>
                  <TableHead className="text-white font-semibold text-xs xl:text-sm">
                    <button 
                      onClick={() => handleSort('llc_type')}
                      className="flex items-center gap-1 hover:text-amber-300 transition-colors"
                    >
                      Type
                      {sortConfig.key === 'llc_type' ? (
                        sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3 text-amber-400" /> : <ArrowDown className="w-3 h-3 text-amber-400" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 text-slate-400" />
                      )}
                    </button>
                  </TableHead>
                  {user?.role !== 'viewer' && (
                    <TableHead className="text-white font-semibold text-xs xl:text-sm print:hidden">
                      <button 
                        onClick={() => handleSort('status')}
                        className="flex items-center gap-1 hover:text-slate-200 transition-colors"
                      >
                        Status
                        {sortConfig.key === 'status' ? (
                          sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        ) : (
                          <ArrowUpDown className="w-3 h-3 text-slate-400" />
                        )}
                      </button>
                    </TableHead>
                  )}
                  {user?.role === 'viewer' && (
                    <TableHead className="text-white font-semibold text-right text-xs xl:text-sm">
                      <span className="hidden xl:inline">Ownership</span>
                      <span className="xl:hidden">Own%</span>
                    </TableHead>
                  )}
                  <TableHead className="text-white font-semibold text-right text-xs xl:text-sm">
                    <button 
                      onClick={() => handleSort('cash_balance')}
                      className="flex items-center gap-1 hover:text-slate-200 transition-colors ml-auto"
                    >
                      <span className="hidden xl:inline">Cash Balance</span>
                      <span className="xl:hidden">Cash</span>
                      {sortConfig.key === 'cash_balance' ? (
                        sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 text-slate-400" />
                      )}
                    </button>
                  </TableHead>
                  <TableHead className="text-white font-semibold text-right text-xs xl:text-sm">
                    <button 
                      onClick={() => handleSort('total_assets')}
                      className="flex items-center gap-1 hover:text-slate-200 transition-colors ml-auto"
                    >
                      <span className="hidden xl:inline">Total Assets</span>
                      <span className="xl:hidden">Assets</span>
                      {sortConfig.key === 'total_assets' ? (
                        sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 text-slate-400" />
                      )}
                    </button>
                  </TableHead>
                  <TableHead className="text-white font-semibold text-right text-xs xl:text-sm">
                    <button 
                      onClick={() => handleSort('third_party_liabilities')}
                      className="flex items-center gap-1 hover:text-slate-200 transition-colors ml-auto"
                    >
                      <span className="hidden xl:inline">3P Liabilities</span>
                      <span className="xl:hidden">3P Liab.</span>
                      {sortConfig.key === 'third_party_liabilities' ? (
                        sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 text-slate-400" />
                      )}
                    </button>
                  </TableHead>
                  <TableHead className="text-white font-semibold text-right text-xs xl:text-sm">
                    <span className="hidden xl:inline">My Value</span>
                    <span className="xl:hidden">Value</span>
                  </TableHead>
                  <TableHead className="text-white font-semibold text-center text-xs xl:text-sm">
                    <button 
                      onClick={() => handleSort('last_activity_date')}
                      className="flex items-center gap-1 hover:text-slate-200 transition-colors mx-auto print:gap-0"
                    >
                      <span className="hidden print:inline">Activity</span>
                      <span className="print:hidden hidden xl:inline">Last Activity</span>
                      <span className="print:hidden xl:hidden">Activity</span>
                      {sortConfig.key === 'last_activity_date' ? (
                        sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3 print:hidden" /> : <ArrowDown className="w-3 h-3 print:hidden" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 text-slate-400 print:hidden" />
                      )}
                    </button>
                  </TableHead>
                  <TableHead className="text-white font-semibold text-center text-xs xl:text-sm">
                    <button 
                      onClick={() => handleSort('last_accounting_date')}
                      className="flex items-center gap-1 hover:text-slate-200 transition-colors mx-auto print:gap-0"
                    >
                      <span className="hidden print:inline">Accounting</span>
                      <span className="print:hidden hidden xl:inline">Last Accounting</span>
                      <span className="print:hidden xl:hidden">Acctg</span>
                      {sortConfig.key === 'last_accounting_date' ? (
                        sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3 print:hidden" /> : <ArrowDown className="w-3 h-3 print:hidden" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 text-slate-400 print:hidden" />
                      )}
                    </button>
                  </TableHead>
                  {user?.role !== 'viewer' && (
                    <>
                    </>
                  )}
                  <TableHead className="w-[40px] print:hidden"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedLLCs.map((llc) => (
                  <TableRow 
                    key={llc.id} 
                    className={`cursor-pointer hover:bg-slate-50 ${llc.info_current || llc.distributions_current ? 'font-semibold' : ''}`}
                    style={llc.distributions_current ? {
                      backgroundColor: 'rgba(191, 219, 254, 0.3)',
                      borderLeft: '3px solid #3b82f6'
                    } : llc.info_current ? { 
                      backgroundColor: 'rgba(254, 243, 199, 0.35)',
                      borderLeft: '3px solid #f59e0b'
                    } : {}}
                    data-testid={`llc-row-${llc.id}`}
                  >
                    <TableCell className="py-1.5">
                      <Link to={`/llc/${llc.id}`} className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${
                          llc.llc_type === 'real_estate' ? 'bg-slate-100' : 'bg-amber-100'
                        }`}>
                          {llc.llc_type === 'real_estate' ? (
                            <Building2 className="w-3.5 h-3.5 text-slate-700" />
                          ) : (
                            <Briefcase className="w-3.5 h-3.5 text-amber-700" />
                          )}
                        </div>
                        <span className="font-medium text-slate-900 whitespace-nowrap">{llc.name}</span>
                        {llc.info_current && (
                          <Star className="w-4 h-4 fill-amber-400 text-amber-400 flex-shrink-0" title="Information verified and current" />
                        )}
                        {llc.distributions_current && (
                          <Star className="w-4 h-4 fill-blue-400 text-blue-400 flex-shrink-0" title="All distributions and admin is current" />
                        )}
                      </Link>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <span className="capitalize print:text-[10px]">{llc.llc_type.replace('_', ' ')}</span>
                    </TableCell>
                    {user?.role !== 'viewer' && (
                      <TableCell className="print:hidden">{getStatusBadge(llc.status)}</TableCell>
                    )}
                    {user?.role === 'viewer' && (
                      <TableCell className="text-right">
                        {userOwnerships[llc.id] ? (
                          llc.hide_percentages ? (
                            <span className="text-slate-400 italic">Calculated</span>
                          ) : (
                            <span className="font-mono text-amber-700">
                              {hideNumbers ? '••••' : `${Number(userOwnerships[llc.id]).toFixed(2)}%`}
                            </span>
                          )
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell className="text-right font-mono">
                      {hideNumbers ? '••••••' : formatCurrency(llc.cash_balance)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-emerald-700">
                      {hideNumbers ? '••••••' : formatCurrency(llc.total_assets)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-rose-600">
                      {hideNumbers ? '••••••' : formatCurrency(llc.third_party_liabilities || 0)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-indigo-700">
                      {(() => {
                        const ownership = userOwnerships[llc.id];
                        if (!ownership) return <span className="text-slate-400">—</span>;
                        
                        // Check for custom valuation values in localStorage
                        const savedRatePerSqft = localStorage.getItem(`valuation_ratePerSqft_${llc.id}`);
                        const savedCapRate = localStorage.getItem(`valuation_capRate_${llc.id}`);
                        
                        // Calculate valuation based on LLC type
                        let netValue = 0;
                        if (llc.llc_type === 'real_estate' && llc.lease_sqft) {
                          // Use saved values or defaults
                          const ratePerSqft = savedRatePerSqft ? parseFloat(savedRatePerSqft) : llc.lease_rate_per_sqft;
                          const capRatePercent = savedCapRate ? parseFloat(savedCapRate) : 6;
                          
                          if (ratePerSqft) {
                            // Real estate: (Annual Rents / Cap Rate) + Cash - Third Party Liabilities
                            const annualRents = llc.lease_sqft * ratePerSqft;
                            const capRate = capRatePercent / 100;
                            const grossValue = annualRents / capRate;
                            const cashBalance = llc.cash_balance || 0;
                            const liabilities = llc.third_party_liabilities || 0;
                            netValue = grossValue + cashBalance - liabilities;
                          }
                        } else {
                          // Cash balance LLC or no lease info: use cash balance
                          netValue = llc.cash_balance || 0;
                        }
                        
                        const myValue = netValue * (ownership / 100);
                        return hideNumbers ? '••••••' : formatCurrency(myValue);
                      })()}
                    </TableCell>
                    <TableCell className="text-center print:py-1">
                      {(() => {
                        if (!llc.last_activity_date || llc.last_activity_date === '-') return <span className="text-slate-400">-</span>;
                        
                        // Parse date without timezone issues
                        const [year, month, day] = llc.last_activity_date.split('-').map(Number);
                        const activityDate = new Date(year, month - 1, day);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        
                        const daysDiff = Math.floor((today - activityDate) / (24 * 60 * 60 * 1000));
                        
                        let colorClass = 'text-slate-500'; // default
                        if (daysDiff <= 30) {
                          colorClass = 'text-emerald-700 print:text-black print:font-semibold'; // Recent - green
                        } else if (daysDiff <= 90) {
                          colorClass = 'text-amber-600 print:text-black'; // 31-90 days - yellow
                        } else if (daysDiff > 365) {
                          colorClass = 'text-rose-600 print:text-black print:italic'; // Over a year - red
                        }
                        
                        return (
                          <span className={colorClass}>
                            {llc.last_activity_date}
                          </span>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-center print:py-1">
                      {(() => {
                        if (!llc.last_accounting_date || llc.last_accounting_date === '-') return <span className="text-slate-400">-</span>;
                        
                        // Parse date without timezone issues
                        const [year, month, day] = llc.last_accounting_date.split('-').map(Number);
                        const accountingDate = new Date(year, month - 1, day);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        
                        const daysDiff = Math.floor((today - accountingDate) / (24 * 60 * 60 * 1000));
                        
                        let colorClass = 'text-slate-500'; // default
                        if (daysDiff <= 30) {
                          colorClass = 'text-emerald-700 print:text-black print:font-semibold'; // Recent - green
                        } else if (daysDiff <= 90) {
                          colorClass = 'text-amber-600 print:text-black'; // 31-90 days - yellow
                        } else if (daysDiff > 365) {
                          colorClass = 'text-rose-600 print:text-black print:italic'; // Over a year - red
                        }
                        
                        return (
                          <span className={colorClass}>
                            {llc.last_accounting_date}
                          </span>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="print:hidden">
                      <Link to={`/llc/${llc.id}`}>
                        <ArrowRight className="w-4 h-4 text-slate-400 hover:text-amber-700" />
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </Card>
        ) : (
          /* Map View */
          <div className="space-y-4">
            {llcsWithAddresses.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="py-12 text-center">
                  <MapPin className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700">No Properties with Addresses</h3>
                  <p className="text-slate-500 mt-1">Add property addresses to your LLCs to see them on the map</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Map Controls */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {geocodingInProgress && (
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading map locations...
                      </div>
                    )}
                    {mapViewMode === 'nearby' && userLocation && (
                      <div className="text-sm text-blue-600">
                        Showing properties within 300 miles of your location
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant={mapViewMode === 'nearby' ? 'default' : 'outline'}
                      size="sm"
                      onClick={toggleLocationView}
                      disabled={locationLoading}
                      className={mapViewMode === 'nearby' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                      data-testid="map-nearby-btn"
                    >
                      {locationLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Navigation className="w-4 h-4 mr-2" />
                      )}
                      {mapViewMode === 'nearby' ? 'Hide Radius' : 'My Location (300mi)'}
                    </Button>
                    <Button
                      variant={mapViewMode === 'all' ? 'default' : 'outline'}
                      size="sm"
                      onClick={showAllLocations}
                      className={mapViewMode === 'all' ? 'bg-slate-900 hover:bg-slate-800' : ''}
                      data-testid="map-all-btn"
                    >
                      <Maximize2 className="w-4 h-4 mr-2" />
                      All Locations
                    </Button>
                  </div>
                </div>

                <Card className="border-slate-200 overflow-hidden">
                  <div className="h-[500px] w-full" data-testid="map-container">
                    <MapContainer
                      center={mapCenter}
                      zoom={4}
                      style={{ height: '100%', width: '100%' }}
                      scrollWheelZoom={true}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      
                      {/* Fit bounds or center on user location */}
                      {mapViewMode === 'all' && mapBounds && mapBounds.length > 0 && (
                        <FitBounds bounds={mapBounds} />
                      )}
                      {mapViewMode === 'nearby' && userLocation && (
                        <CenterOnLocation center={[userLocation.lat, userLocation.lng]} zoom={7} />
                      )}
                      
                      {/* User location marker and radius */}
                      {userLocation && mapViewMode === 'nearby' && (
                        <>
                          <Circle
                            center={[userLocation.lat, userLocation.lng]}
                            radius={MILES_300_IN_METERS}
                            pathOptions={{ 
                              color: '#3b82f6', 
                              fillColor: '#3b82f6', 
                              fillOpacity: 0.1,
                              weight: 2
                            }}
                          />
                          <Marker 
                            position={[userLocation.lat, userLocation.lng]}
                            icon={L.divIcon({
                              className: 'user-location-marker',
                              html: '<div style="background: #3b82f6; width: 16px; height: 16px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
                              iconSize: [16, 16],
                              iconAnchor: [8, 8]
                            })}
                          >
                            <Popup>
                              <div className="text-center">
                                <p className="font-semibold text-blue-600">Your Location</p>
                                <p className="text-sm text-slate-500">300 mile radius shown</p>
                              </div>
                            </Popup>
                          </Marker>
                        </>
                      )}
                      
                      {/* Property markers */}
                      {llcsWithAddresses.map((llc) => {
                        const coords = geocodedLLCs[llc.id];
                        if (!coords) return null;
                        return (
                          <Marker key={llc.id} position={[coords.lat, coords.lng]}>
                            <Popup>
                              <div className="min-w-[200px]">
                                <h3 className="font-semibold text-slate-900">{llc.name}</h3>
                                <p className="text-sm text-slate-600 mt-1">{llc.property_address}</p>
                                <div className="mt-2 pt-2 border-t border-slate-100">
                                  <p className="text-sm">
                                    <span className="text-slate-500">Balance:</span>{' '}
                                    <span className="font-mono font-medium">{formatCurrency(llc.cash_balance)}</span>
                                  </p>
                                </div>
                                <Link
                                  to={`/llc/${llc.id}`}
                                  className="mt-2 inline-flex items-center text-sm text-amber-700 hover:text-amber-800 font-medium"
                                >
                                  View Details <ArrowRight className="w-3 h-3 ml-1" />
                                </Link>
                              </div>
                            </Popup>
                          </Marker>
                        );
                      })}
                    </MapContainer>
                  </div>
                </Card>
                
                {/* Property List below map */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  {llcsWithAddresses.map((llc) => (
                    <Link key={llc.id} to={`/llc/${llc.id}`}>
                      <Card className="border-slate-200 card-hover">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                              <Building2 className="w-5 h-5 text-slate-700" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-slate-900 truncate">{llc.name}</h3>
                              <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                                <MapPin className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate">{llc.property_address}</span>
                              </p>
                            </div>
                            <Badge variant="outline" className={
                              llc.status === 'active' ? 'bg-emerald-100 text-emerald-800' :
                              llc.status === 'liquidating' ? 'bg-amber-100 text-amber-800' :
                              'bg-slate-100 text-slate-600'
                            }>
                              {llc.status}
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Print Footer - Only visible when printing */}
      <div className="hidden print:block mt-8 pt-4 border-t border-slate-300">
        <div className="flex items-center justify-between text-xs text-slate-600">
          <div className="font-medium">
            GCBS Management - Portfolio Overview
          </div>
          <div className="font-mono">
            Generated: {new Date().toLocaleDateString('en-US', { 
              timeZone: 'America/Chicago',
              weekday: 'long',
              year: 'numeric', 
              month: 'long', 
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        </div>
        <div className="text-center text-[10px] text-slate-400 mt-2">
          Confidential - For authorized use only
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
